import { Button } from "@/components/ui/Button";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "../../api/client";
import { getAllNotes } from "../../lib/db";
import { resolveConflict } from "../../lib/sync";
import { Sentry } from "../../lib/sentry";
import type { Note } from "../../types";
import { useUIStore } from "../../store/ui";
import { Icon } from "@/components/ui/Icon";

/**
 * Settings → Conflicts, opened from the sync status. Mirrors the Swift `ConflictResolutionView`
 * UX: step through the conflicted notes one at a time and, for each, keep the
 * whole local version or the whole server version (no per-hunk merge — the
 * non-overlapping edits were already auto-merged silently on push). Base-less
 * notes and truly overlapping edits both land here.
 */
type ConflictNote = Note & { syncStatus?: string };

export function ConflictsSettings() {
  const { t } = useTranslation();
  const openSettings = useUIStore((s) => s.openSettings);
  const [conflicts, setConflicts] = useState<ConflictNote[]>([]);
  const [index, setIndex] = useState(0);
  const [serverNote, setServerNote] = useState<Note | null>(null);
  const [serverError, setServerError] = useState(false);
  const [resolving, setResolving] = useState(false);

  // Snapshot the conflicted notes when the page opens; step through by index
  // (matches Swift). Resolving advances rather than re-querying.
  useEffect(() => {
    setConflicts(getAllNotes().filter((n) => n.syncStatus === "conflict"));
    setIndex(0);
  }, []);

  const current = conflicts[index];
  const currentId = current?.id;

  // Fetch the server version (theirs) for the current note.
  useEffect(() => {
    if (!currentId) {
      setServerNote(null);
      return;
    }
    let cancelled = false;
    setServerNote(null);
    setServerError(false);
    apiFetch<{ note: Note }>(`/notes/${currentId}`)
      .then((res) => {
        if (cancelled) return;
        if (res?.note) setServerNote(res.note);
        else setServerError(true);
      })
      .catch((err) => {
        if (cancelled) return;
        // Offline/network is expected here; a real failure is worth knowing.
        if (navigator.onLine) Sentry.captureException(err);
        setServerError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [currentId]);

  const advance = useCallback(() => {
    setIndex((i) => {
      const next = i + 1;
      if (next >= conflicts.length) {
        openSettings("root");
        return i;
      }
      return next;
    });
  }, [conflicts.length, openSettings]);

  const handleResolve = useCallback(
    async (resolution: "local" | "server") => {
      if (!currentId || resolving) return;
      setResolving(true);
      try {
        await resolveConflict(currentId, resolution);
      } catch (err) {
        // Individual resolution may fail (e.g. transient 5xx); keep going so
        // the user can still resolve the rest.
        Sentry.captureException(err);
      } finally {
        setResolving(false);
        advance();
      }
    },
    [currentId, resolving, advance],
  );

  const total = conflicts.length;
  const hasConflict = Boolean(current);

  if (!hasConflict) {
    return (
      <p className="py-12 text-center text-[13px] text-muted">{t("conflicts.done")}</p>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3">
        <p className="flex-1 text-[13px] leading-relaxed text-ink-secondary">
          {t("conflicts.description")}
        </p>
        {total > 1 && (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={index <= 0}
              aria-label={t("conflicts.previous")}
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
            >
              <Icon name="chevron-left" size={16} />
            </Button>
            <span className="text-[13px] font-semibold text-ink tabular-nums">
              {t("conflicts.step", { current: index + 1, total })}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={index >= total - 1}
              aria-label={t("conflicts.next")}
              onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
            >
              <Icon name="chevron-right" size={16} />
            </Button>
          </div>
        )}
      </div>

      {/* Two panes: yours vs theirs. Side-by-side on wide, stacked on narrow. */}
      <div className="grid h-[min(420px,60dvh)] grid-cols-1 grid-rows-2 gap-px overflow-hidden rounded-[10px] border border-border-light bg-border-light md:grid-cols-2 md:grid-rows-1">
        <ConflictPane label={t("conflicts.yours")} content={current.content} />
        <ConflictPane
          label={t("conflicts.theirs")}
          content={serverNote?.content ?? null}
          error={serverError ? t("conflicts.serverError") : undefined}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={resolving || serverError || !serverNote}
          onClick={() => handleResolve("server")}
        >
          {t("conflicts.keepTheirs")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={resolving}
          onClick={() => handleResolve("local")}
        >
          {t("conflicts.keepYours")}
        </Button>
      </div>
    </>
  );
}

function ConflictPane({
  label,
  content,
  error,
}: {
  label: string;
  content: string | null;
  error?: string;
}) {
  return (
    <div className="flex flex-col min-h-0 bg-canvas">
      <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted border-b border-border-light">
        {label}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
        {error ? (
          <div className="text-[13px] text-danger">{error}</div>
        ) : content === null ? (
          <div className="text-[13px] text-muted" />
        ) : (
          <pre className="whitespace-pre-wrap break-words font-prose text-[13px] leading-relaxed text-ink">
            {content}
          </pre>
        )}
      </div>
    </div>
  );
}
